import { ApplicationRef, ComponentRef, createComponent, Injectable } from '@angular/core';
import * as xlsx from 'xlsx';
import { GoogleDriveService } from '../GoogleDrive/google-drive.service';
import { PluginsManager } from 'src/app/managers/plugins.manager';
import { ExportDialogComponent } from 'src/app/dialogs/export-dialog/export-dialog.component';
import { MainComponent } from 'src/app/components/main/main.component';
import { jqxGridComponent } from 'jqwidgets-ng/jqxgrid';
import { AppComponent } from 'src/app/app.component';
import { GridComponent } from 'movisat-common-gui';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private exportDialogComponent: ComponentRef<ExportDialogComponent>;

  public constructor(
    private googleDriveService: GoogleDriveService,
    private pluginsManager: PluginsManager,
    private appRef: ApplicationRef,
  ) { }

  protected translate(text: string): string {
    return AppComponent.translate(text);
  }

  private isJqxGridComponent(grid: jqxGridComponent | GridComponent): grid is jqxGridComponent {
    return typeof (grid as jqxGridComponent).getrows === 'function';
  }

  private isGridComponent(grid: jqxGridComponent | GridComponent): grid is GridComponent {
    return typeof (grid as GridComponent).getRows === 'function';
  }

  public async exportFile(gridData: jqxGridComponent | GridComponent, fileName: string = 'export.xlsx'): Promise<void> {
    const file = this.generateExcelFile(gridData);

    const rows = this.isGridComponent(gridData) ? gridData.getRows() : gridData.getrows();

    if (!this.pluginsManager.isPluginActive('Google Drive')) {
      return this.downloadFile(file, fileName);
    }

    if (rows.length === 0) {
      return MainComponent.getInstance().showWarning('ATENCION', this.translate('No_existen_datos'), 2000);
    }

    const action = await this.createExportDialog();
    if (action === 'download') {
      this.downloadFile(file, fileName);
    } else if (action === 'google-drive') {
      await this.uploadToGoogleDrive(file, fileName);
    }
  }

  private createExportDialog(): Promise<string> {
    return new Promise((resolve) => {
      if (this.appRef.viewCount > 1) {
        this.exportDialogComponent.destroy()
      }

      this.exportDialogComponent = createComponent(ExportDialogComponent, {
        environmentInjector: this.appRef.injector,
      });
      this.appRef.attachView(this.exportDialogComponent.hostView);

      this.exportDialogComponent.instance.actionChosen.subscribe((action: string) => {
        resolve(action);
        this.exportDialogComponent.destroy();
      });
    });
  }

  private downloadFile(file: Blob, fileName: string): void {
    if (!file) {
      console.error('No se pudo generar el archivo.');
      return;
    }

    const linkTag = document.createElement('a');
    linkTag.href = URL.createObjectURL(file);
    linkTag.download = fileName;
    document.body.appendChild(linkTag);
    linkTag.click();
    document.body.removeChild(linkTag);
  }

  private async uploadToGoogleDrive(file: Blob, fileName: string): Promise<void> {
    try {
      const isPluginActive = this.pluginsManager.isPluginActive("Google Drive");
      if (!isPluginActive) {
        MainComponent.getInstance().showError(
          'ATENCION',
          'GoogleDrive_deshabilitado',
          2000
        );
        return;
      }

      await this.googleDriveService.signIn();

      if (!file) {
        console.error('No se pudo generar el archivo.');
        return;
      }

      const folderId = await this.googleDriveService.pickFolder();

      if (!folderId) {
        return;
      }

      await this.googleDriveService.uploadFile(file, fileName, folderId);
      MainComponent.getInstance().showSuccess(
        'ATENCION',
        'GoogleDrive_subido',
        2000
      );
    } catch (error) {
      console.error('Error al exportar a Google Drive:', error);
      MainComponent.getInstance().showError(
        'ATENCION',
        'GoogleDrive_error',
        2000
      );
    }
  }

  public generateExcelFile(gridData: jqxGridComponent | GridComponent): Blob {
    let json: string;

    if (this.isGridComponent(gridData)) {
      json = (gridData.exportData('json') as unknown) as string;
    } else if (this.isJqxGridComponent(gridData)) {
      json = gridData.exportdata('json');
    }

    const datos = JSON.parse(json);
    const ws: xlsx.WorkSheet = xlsx.utils.json_to_sheet(datos);
    ws['!autofilter'] = { ref: ws['!ref'] };

    const wb: xlsx.WorkBook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, 'Hoja1');

    const excelBuffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }
}
